import { ProfileWithJoins } from '../../custom_types'
import { toUtcDateOnlyString } from '@dolfin/utils/dates'
import { Enums, Tables } from '../../db_types'
import {
  CurrentTeamMembershipSetupRow,
  CurrentOrganizationProfileData,
  DolfinRpc,
  ProfilesResult,
  ProviderProfileIdentity,
  SaveUserEditTeamMembershipRangeSetupParams,
  TeamMembershipHistorySetupRow,
  TeamSetupMemberRow
} from '../../rpc'

type ActiveProfileDataAtDate = Pick<Tables<'organizations_profiles_data'>, 'type'>
export type GetProfilesParams = {
  from?: number
  to?: number
}

export type GetActiveProfilesDataParams = {
  businessUnitIds?: number[]
  date: Date | string
  organizationId: number
  profileIds?: string[]
}

export type UserEditTeamMembershipManager = Pick<
  TeamSetupMemberRow,
  'profile_id' | 'name' | 'email' | 'img_url'
>

export type UserEditCurrentTeamAssignment = {
  effective_from: string | null
  full_view_access: boolean
  managers: UserEditTeamMembershipManager[]
  role: Enums<'team_role'>
  team_id: number
  team_name: string | null
}

export type UserEditHistoricalTeamAssignment = Pick<
  TeamMembershipHistorySetupRow,
  'effective_from' | 'effective_to' | 'full_view_access' | 'role' | 'team_id' | 'team_name'
>

export type UserEditTeamMembershipHistory = {
  currentManagements: UserEditCurrentTeamAssignment[]
  currentMemberships: UserEditCurrentTeamAssignment[]
  historicalManagements: UserEditHistoricalTeamAssignment[]
  historicalMemberships: UserEditHistoricalTeamAssignment[]
}

export default class ProfilesDomain {
  private readonly rpcClient: Pick<
    DolfinRpc,
    | 'getActiveProfileDataAtDate'
    | 'getActiveOrganizationProfilesData'
    | 'getAdminProviderProfileIdentity'
    | 'getOrganizationProfilesData'
    | 'getProfileForEdit'
    | 'getProviderProfileIdentity'
    | 'getProfiles'
    | 'listMemberMembershipHistorySetup'
    | 'listProfileCurrentTeamMembershipsSetup'
    | 'listTeamSetupMembers'
    | 'saveUserEditTeamMembershipRangeSetup'
  >

  constructor({
    rpcClient
  }: {
    rpcClient: Pick<
      DolfinRpc,
      | 'getActiveProfileDataAtDate'
      | 'getActiveOrganizationProfilesData'
      | 'getAdminProviderProfileIdentity'
      | 'getOrganizationProfilesData'
      | 'getProfileForEdit'
      | 'getProviderProfileIdentity'
      | 'getProfiles'
      | 'listMemberMembershipHistorySetup'
      | 'listProfileCurrentTeamMembershipsSetup'
      | 'listTeamSetupMembers'
      | 'saveUserEditTeamMembershipRangeSetup'
    >
  }) {
    this.rpcClient = rpcClient
  }

  async getProfiles(params?: GetProfilesParams): Promise<ProfilesResult> {
    return this.rpcClient.getProfiles(params)
  }

  async getProfileForEdit(
    profileId: string,
    organizationId: number
  ): Promise<ProfileWithJoins | null> {
    return this.rpcClient.getProfileForEdit(organizationId, profileId)
  }

  async getProfileData(
    profileId: string,
    organizationId: number
  ): Promise<Tables<'organizations_profiles_data'>[]> {
    const profilesData = await this.getProfilesData([profileId], organizationId)

    return profilesData[profileId] ?? []
  }

  async getProfilesData(
    profileIds: string[],
    organizationId: number
  ): Promise<Record<string, Tables<'organizations_profiles_data'>[]>> {
    return this.rpcClient.getOrganizationProfilesData(organizationId, profileIds)
  }

  async getActiveProfileDataAtDate(
    organizationId: number,
    profileId: string,
    date: Date | string
  ): Promise<ActiveProfileDataAtDate | null> {
    const formattedDate = typeof date === 'string' ? date : toUtcDateOnlyString(date)

    return this.rpcClient.getActiveProfileDataAtDate(profileId, formattedDate, organizationId)
  }

  async getActiveProfilesData({
    organizationId,
    date,
    profileIds,
    businessUnitIds
  }: GetActiveProfilesDataParams): Promise<CurrentOrganizationProfileData[]> {
    const formattedDate = typeof date === 'string' ? date : toUtcDateOnlyString(date)
    const activeProfilesData = await this.rpcClient.getActiveOrganizationProfilesData(
      organizationId,
      formattedDate,
      profileIds
    )

    if (businessUnitIds == null || businessUnitIds.length === 0) {
      return activeProfilesData
    }

    return activeProfilesData.filter(
      profileData =>
        profileData.business_unit_id != null &&
        businessUnitIds.includes(profileData.business_unit_id)
    )
  }

  async getProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null> {
    return this.rpcClient.getProviderProfileIdentity(providerProfileId, providerSpaceId)
  }

  async getAdminProviderProfileIdentity(
    providerProfileId: string,
    providerSpaceId: string
  ): Promise<ProviderProfileIdentity | null> {
    return this.rpcClient.getAdminProviderProfileIdentity(providerProfileId, providerSpaceId)
  }

  async saveUserEditTeamMembershipRange(
    args: SaveUserEditTeamMembershipRangeSetupParams
  ): Promise<void> {
    await this.rpcClient.saveUserEditTeamMembershipRangeSetup(args)
  }

  async getUserEditTeamMembershipHistory({
    organizationId,
    profileId
  }: {
    organizationId: number
    profileId: string
  }): Promise<UserEditTeamMembershipHistory> {
    const [membershipSnapshots, history] = await Promise.all([
      this.rpcClient.listProfileCurrentTeamMembershipsSetup(organizationId, profileId),
      this.rpcClient.listMemberMembershipHistorySetup(organizationId, profileId)
    ])

    const currentPeriodStartByTeamId = new Map<number, string | null>(
      history
        .filter(row => row.status === 'active' && row.effective_to == null && row.role != null)
        .map(row => [row.team_id, row.effective_from])
    )

    const currentMembershipsByTeamId = new Map<number, CurrentTeamMembershipSetupRow>()

    history.forEach(row => {
      if (row.status !== 'active' || row.effective_to != null || row.role == null) {
        return
      }

      const snapshot = membershipSnapshots.find(membership => membership.team_id === row.team_id)

      currentMembershipsByTeamId.set(row.team_id, {
        profile_id: row.profile_id,
        profile_name: row.profile_name,
        profile_email: row.profile_email,
        team_id: row.team_id,
        team_name: row.team_name,
        role: row.role,
        full_view_access: row.full_view_access ?? snapshot?.full_view_access ?? false
      } satisfies CurrentTeamMembershipSetupRow)
    })

    const teamsWithAnyHistory = new Set(history.map(row => row.team_id))

    membershipSnapshots.forEach(snapshot => {
      if (
        currentMembershipsByTeamId.has(snapshot.team_id) ||
        teamsWithAnyHistory.has(snapshot.team_id)
      ) {
        return
      }

      currentMembershipsByTeamId.set(snapshot.team_id, snapshot)
    })

    const currentMemberships = Array.from(currentMembershipsByTeamId.values())

    const managerRowsByTeamId = new Map<number, UserEditTeamMembershipManager[]>()
    const currentTeamIds = currentMemberships.map(row => row.team_id)

    if (currentTeamIds.length > 0) {
      const teamMembers = await Promise.all(
        currentTeamIds.map(teamId => this.rpcClient.listTeamSetupMembers(organizationId, teamId))
      )

      teamMembers.forEach(rows => {
        const teamId = rows[0]?.team_id

        if (teamId == null) {
          return
        }

        managerRowsByTeamId.set(
          teamId,
          rows
            .filter(row => row.role === 'manager')
            .map(row => ({
              profile_id: row.profile_id,
              name: row.name,
              email: row.email,
              img_url: row.img_url
            }))
        )
      })
    }

    const mapCurrentAssignment = (
      row: CurrentTeamMembershipSetupRow
    ): UserEditCurrentTeamAssignment => ({
      effective_from: currentPeriodStartByTeamId.get(row.team_id) ?? null,
      full_view_access: row.full_view_access,
      managers: managerRowsByTeamId.get(row.team_id) ?? [],
      role: row.role,
      team_id: row.team_id,
      team_name: row.team_name
    })

    const mapHistoricalAssignment = (
      row: TeamMembershipHistorySetupRow
    ): UserEditHistoricalTeamAssignment => ({
      effective_from: row.effective_from,
      effective_to: row.effective_to,
      full_view_access: row.full_view_access,
      role: row.role,
      team_id: row.team_id,
      team_name: row.team_name
    })

    const historicalRows = history.filter(row => row.status === 'active')

    return {
      currentManagements: currentMemberships
        .filter(row => row.role === 'manager')
        .map(mapCurrentAssignment),
      currentMemberships: currentMemberships
        .filter(row => row.role === 'member')
        .map(mapCurrentAssignment),
      historicalManagements: [],
      historicalMemberships: historicalRows
        .filter(row => row.role !== 'manager')
        .map(mapHistoricalAssignment)
    }
  }
}
